import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { MeasureArea } from '../../../api/workEnvironmentTypes';
import { areasByNeed } from '../measuresView';
import { areaName, LEVEL_COLORS } from '../riskView';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { areas: MeasureArea[]; onPropose: (areaKey: string) => void };

/** Every area with high or moderate risk should get at least one proposal before the deadline */
export const MeasureAreasCard = ({ areas, onPropose }: Props): ReactElement => {
  const { t } = useTranslation();
  const { may } = useSurveyActions();
  return (
    <section aria-labelledby="measure-areas-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="measure-areas-heading">
            {t('workEnvironmentMeasures.areas.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentMeasures.areas.intro')}
          </Paragraph>
        </div>
        <ul className={classes.dataAreas}>
          {areasByNeed(areas).needing.map((area) => (
            <li key={area.key} className={classes.dataArea}>
              <div className={classes.areaSummary}>
                <span className={classes.areaTitle}>
                  <span className="ds-label">{areaName(area, t)}</span>
                  <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                    {area.proposals > 0
                      ? t('workEnvironmentMeasures.areas.proposals', { count: area.proposals })
                      : t('workEnvironmentMeasures.areas.none')}
                  </span>
                </span>
                <span className={classes.areaTags}>
                  <Tag data-size="sm" data-color={LEVEL_COLORS[area.level]}>
                    {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                  </Tag>
                  {may('propose') && (
                    <Button variant="tertiary" data-size="sm" onClick={() => onPropose(area.key)}>
                      {t('workEnvironmentMeasures.areas.propose')}
                    </Button>
                  )}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
